import { UserManager, WebStorageStateStore, User, UserManagerSettings } from 'oidc-client-ts';

// Create a secure session storage implementation that persists during browser session
// but is cleared when browser is closed (more secure than localStorage)
class SecureSessionStorage implements Storage {
  private storage: Storage;

  constructor() {
    this.storage = window.sessionStorage;
  }

  getItem(key: string): string | null {
    return this.storage.getItem(key);
  }

  setItem(key: string, value: string): void {
    this.storage.setItem(key, value);
  }

  removeItem(key: string): void {
    this.storage.removeItem(key);
  }

  clear(): void {
    this.storage.clear();
  }

  key(index: number): string | null {
    return this.storage.key(index);
  }

  get length(): number {
    return this.storage.length;
  }
}

// Create a singleton instance of the secure session storage
const secureSessionStorage = new SecureSessionStorage();

// Configuration for the OIDC client
const oidcConfig: UserManagerSettings = {
  authority: process.env.REACT_APP_ZITADEL_ISSUER,
  client_id: process.env.REACT_APP_ZITADEL_CLIENT_ID,
  redirect_uri: process.env.REACT_APP_ZITADEL_REDIRECT_URI && process.env.REACT_APP_ZITADEL_REDIRECT_URI.trim()
    ? process.env.REACT_APP_ZITADEL_REDIRECT_URI
    : `${window.location.origin}/auth/signin`,
  post_logout_redirect_uri: process.env.REACT_APP_ZITADEL_POST_LOGOUT_REDIRECT_URI && process.env.REACT_APP_ZITADEL_POST_LOGOUT_REDIRECT_URI.trim()
    ? process.env.REACT_APP_ZITADEL_POST_LOGOUT_REDIRECT_URI
    : `${window.location.origin}/auth/signout`,
  response_type: 'code',
  scope: 'openid profile email offline_access urn:zitadel:iam:user:metadata',

  // Using session storage for persistence during browser session
  // More secure than localStorage but persists through page refreshes
  userStore: new WebStorageStateStore({ store: secureSessionStorage }),

  // Additional settings
  automaticSilentRenew: true,
  loadUserInfo: true,
};

// Create the UserManager instance with the configuration
const userManager = new UserManager(oidcConfig);

// Log OIDC events for debugging
userManager.events.addUserLoaded(() => {
  // User loaded successfully
});

userManager.events.addSilentRenewError((error) => {
  console.error('Silent renew error:', error);
});

userManager.events.addUserSignedOut(() => {
  userManager.removeUser();
});

// Define the interface for the auth service
interface AuthService {
  getUserManager: () => UserManager;
  signin: () => Promise<void>;
  signout: () => Promise<void>;
  handleSignInCallback: () => Promise<User>;
  getUser: () => Promise<User | null>;
  isAuthenticated: () => Promise<boolean>;
  getAccessToken: () => Promise<string | null>;
  getIdToken: () => Promise<string | null>;
  getUserProfile: () => Promise<any | null>;
}

// Export the authentication service functions
const authService: AuthService = {
  // Get the UserManager instance
  getUserManager: () => {
    return userManager;
  },

  // Initiate the sign in process
  signin: async () => {
    try {
      await userManager.signinRedirect();
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  },

  // Sign out the user
  signout: async () => {
    try {
      // Clean up organization data from sessionStorage
      sessionStorage.removeItem('selectedOrgName');
      sessionStorage.removeItem('selectedOrgId');
      // Clean up username from sessionStorage
      sessionStorage.removeItem('username');

      // Clear all SSO cookies if enabled (default to true)
      if (process.env.REACT_APP_ENABLE_SSO_COOKIES !== 'false') {
        const cookieDomain = process.env.REACT_APP_SSO_COOKIE_DOMAIN || 'localhost';

        // Clear all Workplace SSO cookies
        const cookiesToClear = [
          'access_token',          // Workplace access token
          'refresh_token',         // Workplace refresh token
          'organization_id',       // Organization ID
          'membership_id',         // Membership ID
          'user_id',               // User ID
        ];

        cookiesToClear.forEach(cookieName => {
          document.cookie = `${cookieName}=; Max-Age=0; path=/; domain=${cookieDomain}`;
        });

        console.log('Cleared all SSO cookies');
      }

      await userManager.signoutRedirect();
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  },

  // Handle the redirect callback after sign in
  handleSignInCallback: async () => {
    try {
      // Check if we have auth code and state in sessionStorage
      const code = sessionStorage.getItem('auth_code');
      const state = sessionStorage.getItem('auth_state');
      const timestamp = sessionStorage.getItem('auth_timestamp');
      
      if (code && state && timestamp) {
        // Check if the code is not too old (5 minutes)
        const now = new Date().getTime();
        const fiveMinutes = 5 * 60 * 1000;
        
        if (now - parseInt(timestamp) > fiveMinutes) {
          console.warn('Auth code is too old, removing');
          sessionStorage.removeItem('auth_code');
          sessionStorage.removeItem('auth_state');
          sessionStorage.removeItem('auth_timestamp');
          throw new Error('Authentication code expired');
        }
        
        // Process the authentication with the code
        try {
          // Create a fake URL with the code and state for the userManager to process
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('code', code);
          currentUrl.searchParams.set('state', state);
          
          // Use signinRedirectCallback with the fake URL
          const user = await userManager.signinRedirectCallback(currentUrl.toString());
          
          // Clean up
          sessionStorage.removeItem('auth_code');
          sessionStorage.removeItem('auth_state');
          sessionStorage.removeItem('auth_timestamp');
          
          return user;
        } catch (callbackError) {
          console.error('Error processing callback with stored code:', callbackError);
          
          // If there's an error with the stored code (e.g., after a page refresh),
          // clean up and try to get the current user instead
          sessionStorage.removeItem('auth_code');
          sessionStorage.removeItem('auth_state');
          sessionStorage.removeItem('auth_timestamp');
          
          // Try to get the current user instead
          const currentUser = await userManager.getUser();
          if (currentUser && !currentUser.expired) {
            return currentUser;
          }
          
          // If no current user, rethrow the error
          throw callbackError;
        }
      } else {
        try {
          // No code in localStorage, try normal callback
          const user = await userManager.signinRedirectCallback();
          return user;
        } catch (callbackError) {
          console.error('Error processing normal callback:', callbackError);
          
          // If there's an error with the normal callback (e.g., after a page refresh),
          // try to get the current user instead
          const currentUser = await userManager.getUser();
          if (currentUser && !currentUser.expired) {
            return currentUser;
          }
          
          // If no current user, rethrow the error
          throw callbackError;
        }
      }
    } catch (error) {
      console.error('Handle sign in callback error:', error);
      throw error;
    }
  },

  // Get the current user
  getUser: async () => {
    try {
      const user = await userManager.getUser();
      return user;
    } catch (error) {
      console.error('Get user error:', error);
      throw error;
    }
  },

  // Check if the user is authenticated
  isAuthenticated: async () => {
    try {
      const user = await userManager.getUser();
      return !!user && !user.expired;
    } catch (error) {
      console.error('Is authenticated error:', error);
      return false;
    }
  },

  // Get the access token
  getAccessToken: async () => {
    try {
      const user = await userManager.getUser();
      return user?.access_token || null;
    } catch (error) {
      console.error('Get access token error:', error);
      return null;
    }
  },

  // Get the ID token
  getIdToken: async () => {
    try {
      const user = await userManager.getUser();
      return user?.id_token || null;
    } catch (error) {
      console.error('Get ID token error:', error);
      return null;
    }
  },

  // Get user profile
  getUserProfile: async () => {
    try {
      const user = await userManager.getUser();
      return user?.profile || null;
    } catch (error) {
      console.error('Get user profile error:', error);
      return null;
    }
  },
};

export default authService;
